import { Button, Typography } from "@mui/material";
import Modal from "../../App/Components/Modal/Modal";
import { useArchiveGroupsMutation, useUnarchiveGroupsMutation } from "../../App/Queries/GroupQueries";
import { useNotification } from "../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../App/State/Hooks/useDebouncedHandler";
import { IGroup } from "../../App/Typings/Groups";

export interface ArchiveGroupModalProps {
  isOpen: boolean;
  handleModalClose: () => void;
  group: IGroup;
}

export default function ArchiveGroupModal({ isOpen, handleModalClose, group }: ArchiveGroupModalProps) {
  const { displaySuccess, displayError } = useNotification();
  const { mutateAsync: archiveGroups, isLoading: isArchiveLoading } = useArchiveGroupsMutation();
  const { mutateAsync: unArchiveGroups, isLoading: isUnarchiveLoading } = useUnarchiveGroupsMutation();

  const handleArchiveClick = useDebouncedHandler(async () => {
    await archiveGroups([group.id], {
      onSuccess: () => {
        displaySuccess("Group archived successfully");
        handleModalClose();
      },
      onError: () => {
        displayError("Error archiving group");
      },
    });
  }, 300);

  const handleUnarchiveClick = useDebouncedHandler(async () => {
    await unArchiveGroups([group.id], {
      onSuccess: () => {
        displaySuccess("Group unarchived successfully");
        handleModalClose();
      },
      onError: () => {
        displayError("Error unarchiving group");
      },
    });
  }, 300);

  return (
    <Modal
      dataTestId="archive-dialog"
      handleCloseModal={handleModalClose}
      isOpen={isOpen}
      title={`${group.isArchived ? "Unarchive" : "Archive"} Group: ${group.name}`}
      size="md"
      content={group.isArchived ? <UnarchiveContent /> : <ArchiveContent />}
      footerContent={
        <>
          <Button variant="contained" color="inherit" onClick={handleModalClose}>
            Cancel
          </Button>
          <ArchiveButton
            title={group.isArchived ? "Unarchive" : "Archive"}
            disabled={group.isArchived ? isUnarchiveLoading : isArchiveLoading}
            onClick={group.isArchived ? handleUnarchiveClick : handleArchiveClick}
            dataTestId={group.isArchived ? "unarchive-group-button" : "archive-group-button"}
          />
        </>
      }
    />
  );
}

const ArchiveContent = () => (
  <>
    <Typography component="p" variant="subtitle1" sx={{ fontWeight: "bold" }}>
      Are you sure you want to archive this group?
    </Typography>
    <Typography component="p">
      You will no longer be able to add candidates to this group, all scheduled messages will be cancelled and
      the group will be removed from the group list.
    </Typography>
    <Typography component="p" sx={{ mt: 2 }}>
      Unarchiving this group will make it available for use again.
    </Typography>
  </>
);

const UnarchiveContent = () => (
  <>
    <Typography component="p" variant="subtitle1" sx={{ fontWeight: "bold" }}>
      Are you sure you want to unarchive this group?
    </Typography>
    <Typography component="p">This group will be available for use again.</Typography>
  </>
);

const ArchiveButton = ({
  title,
  disabled,
  onClick,
  dataTestId,
}: {
  title: string;
  disabled: boolean;
  onClick: () => void;
  dataTestId?: string;
}) => (
  <Button variant="contained" color="primary" disabled={disabled} onClick={onClick} data-test={dataTestId}>
    {title}
  </Button>
);
